import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  type ComponentProps,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { TimeAgoMessage } from '@furality/furality-purpose';
import useToasts from '@/lib/useToast.ts';

export type AuctionNotificationToastBodyProps = Omit<ComponentProps<'div'>, 'title'> & {
  id: string;
  timestamp: number;
  auctionId?: string;
  title: ReactNode;
  message: ReactNode;
  linkToList?: boolean;
};

const DISMISS_AFTER_MS = 10_000;
const BROADCAST_CHANNEL_NAME = 'auction-toast-dismiss';

const AuctionNotificationToastBody: FC<AuctionNotificationToastBodyProps> = ({
  id,
  timestamp,
  auctionId,
  title,
  message,
  linkToList = false,

  className,
  ...divProps
}) => {
  const { t } = useTranslation();
  const { removeToast } = useToasts();

  const [expiring, setExpiring] = useState(false);

  const channelRef = useRef<BroadcastChannel | null>(null);
  const expiringTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

    channelRef.current.onmessage = (event: MessageEvent<{ auctionId: string }>) => {
      if (auctionId && event.data.auctionId === auctionId) {
        setExpiring(true);
        removeToast(id);
      }
    };

    return () => {
      channelRef.current?.close();
      channelRef.current = null;

      if (expiringTimeout.current) {
        clearTimeout(expiringTimeout.current);

        expiringTimeout.current = null;
      }
    };
  }, [id, auctionId, removeToast]);

  const dismiss = useCallback(() => {
    if (expiringTimeout.current !== null) {
      clearTimeout(expiringTimeout.current);

      expiringTimeout.current = null;
    }

    if (auctionId && channelRef.current) {
      channelRef.current.postMessage({ auctionId });
    }

    setExpiring(true);
    removeToast(id);
  }, [id, auctionId, removeToast]);

  useEffect(() => {
    expiringTimeout.current = setTimeout(() => {
      dismiss();
    }, DISMISS_AFTER_MS);

    return () => {
      if (expiringTimeout.current) {
        clearTimeout(expiringTimeout.current);

        expiringTimeout.current = null;
      }
    };
  }, [dismiss]);

  return (
    <div className={clsx(expiring && 'toast__fadeout', className)} {...divProps}>
      <div className="toast-header text-white bg-gradient-primary">
        <strong className="mr-auto">{title}</strong>

        {timestamp && <TimeAgoMessage timestampUnix={timestamp} />}

        <button
          type="button"
          className="close unstyled-button ml-2 px-1 fs-4 lh-1"
          aria-label={t('shared.close.aria_label')}
          onClick={dismiss}
        >
          <span className="text-white" aria-hidden="true">
            &times;
          </span>
        </button>
      </div>

      {linkToList ? (
        <Link
          to="/auctions"
          onClick={dismiss}
          className="toast-body text-white bg-light text-decoration-none d-block"
        >
          {message}
        </Link>
      ) : (
        <Link
          to="/auctions/$id"
          params={{ id: auctionId ?? '' }}
          onClick={dismiss}
          className="toast-body text-white bg-light text-decoration-none d-block"
        >
          {message}
        </Link>
      )}
    </div>
  );
};

export default AuctionNotificationToastBody;
